import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  ApiError,
  formatMoney,
  formatQuantity,
  type Holding,
  type Quote,
} from '@greencandle/api-client'
import { api } from '../api'
import { Money, Percent, Quantity } from '../components/Figure'
import { QuotedPrice } from '../components/QuotedPrice'

type SortKey = 'symbol' | 'quantity' | 'costBasis' | 'marketValue' | 'unrealisedPnl' | 'weight'

/**
 * FR-H2. Every position, sortable, groupable by asset class, expanding to its lots.
 *
 * <p>Unpriced holdings are the reason this screen is not simply a sorted table. Eighteen of
 * this account's twenty-nine positions are options that no provider quotes, so a naive sort by
 * market value drops them all to the bottom under an implied value of zero, and a total row
 * would quietly add them in as nothing. They sort last on an unknown value rather than as zero,
 * the group header counts them, and the total says how much of itself it covers.
 */
export function HoldingsPage() {
  const [accountId, setAccountId] = useState<string | null>(null)
  const [sort, setSort] = useState<SortKey>('marketValue')
  const [ascending, setAscending] = useState(false)
  const [grouped, setGrouped] = useState(true)
  const [expanded, setExpanded] = useState<string | null>(null)

  const accounts = useQuery({ queryKey: ['accounts'], queryFn: () => api.listAccounts() })
  const selected = accountId ?? accounts.data?.[0]?.id ?? null

  const holdings = useQuery({
    queryKey: ['holdings', selected],
    queryFn: () => api.holdings(selected!),
    enabled: selected !== null,
    placeholderData: (previous) => previous,
  })

  // Its own query, not folded into holdings. Holdings come from stored closes and always
  // answer; quotes go out to a third party and may not. Asking for them together would make
  // the table wait for the slower and less important of the two.
  const quotes = useQuery({
    queryKey: ['quotes', selected],
    queryFn: () => api.quotes(selected!),
    enabled: selected !== null,
    refetchInterval: 60_000,
    placeholderData: (previous) => previous,
  })

  const quoteFor = useMemo(() => {
    const map = new Map<string, Quote>()
    for (const quote of quotes.data ?? []) map.set(quote.instrumentId, quote)
    return map
  }, [quotes.data])

  const account = accounts.data?.find((a) => a.id === selected) ?? accounts.data?.[0]

  const groups = useMemo(() => {
    const rows = [...(holdings.data ?? [])]
    rows.sort(compare(sort, ascending))
    if (!grouped) {
      return [['All holdings', rows] as const]
    }
    const byClass = new Map<string, Holding[]>()
    for (const row of rows) {
      byClass.set(row.assetClass, [...(byClass.get(row.assetClass) ?? []), row])
    }
    return [...byClass.entries()].sort((a, b) => b[1].length - a[1].length)
  }, [holdings.data, sort, ascending, grouped])

  if (accounts.isPending) return <p className="muted">Loading…</p>
  if (accounts.isError) return <Failed error={accounts.error} />
  if (!account) {
    return (
      <>
        <h1 className="page-title">Holdings</h1>
        <div className="notice">No accounts yet.</div>
      </>
    )
  }

  function toggle(key: SortKey) {
    if (sort === key) {
      setAscending((v) => !v)
    } else {
      setSort(key)
      // Names read naturally A-Z; figures read naturally largest first.
      setAscending(key === 'symbol')
    }
  }

  const all = holdings.data ?? []
  // `!= null`, not `!== null`. The backend serialises with non-null inclusion, so a figure it
  // could not produce is ABSENT from the JSON rather than present as null - it arrives as
  // undefined, and a strict null check reports every unvalued option as valued.
  const priced = all.filter((h) => h.marketValueInBase != null)

  return (
    <>
      <h1 className="page-title">Holdings</h1>
      <p className="page-subtitle">{account.name}</p>

      <div className="filters">
        <select
          aria-label="Account"
          value={account.id}
          onChange={(e) => setAccountId(e.target.value)}
        >
          {accounts.data.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name} ({a.baseCurrency})
            </option>
          ))}
        </select>

        <div className="segmented" role="group" aria-label="Grouping">
          <button type="button" aria-pressed={grouped} onClick={() => setGrouped(true)}>
            By class
          </button>
          <button type="button" aria-pressed={!grouped} onClick={() => setGrouped(false)}>
            Flat
          </button>
        </div>

        <span className="muted">
          {priced.length} of {all.length} valued
        </span>
      </div>

      {holdings.isError ? (
        <Failed error={holdings.error} />
      ) : (
        <div className={`card${holdings.isFetching && !holdings.isPending ? ' refetching' : ''}`}>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <Th label="Symbol" active={sort === 'symbol'} asc={ascending}
                      onClick={() => toggle('symbol')} />
                  <Th label="Quantity" right active={sort === 'quantity'} asc={ascending}
                      onClick={() => toggle('quantity')} />
                  <th className="right">Avg cost</th>
                  <Th label="Cost basis" right active={sort === 'costBasis'} asc={ascending}
                      onClick={() => toggle('costBasis')} />
                  <th className="right">Price</th>
                  <Th label="Market value" right active={sort === 'marketValue'} asc={ascending}
                      onClick={() => toggle('marketValue')} />
                  <Th label="Unrealised" right active={sort === 'unrealisedPnl'} asc={ascending}
                      onClick={() => toggle('unrealisedPnl')} />
                  <Th label="Weight" right active={sort === 'weight'} asc={ascending}
                      onClick={() => toggle('weight')} />
                </tr>
              </thead>

              {groups.map(([name, rows]) => (
                <tbody key={name}>
                  {grouped && (
                    <tr className="group-row">
                      <td colSpan={8}>
                        <strong>{name}</strong>{' '}
                        <span className="muted">
                          {rows.length} position{rows.length === 1 ? '' : 's'}
                          {rows.some((r) => r.marketValue == null) &&
                            ` · ${rows.filter((r) => r.marketValue == null).length} unvalued`}
                        </span>
                      </td>
                    </tr>
                  )}
                  {rows.map((row) => (
                    <Row
                      key={row.instrumentId}
                      row={row}
                      quote={quoteFor.get(row.instrumentId)}
                      accountId={account.id}
                      expanded={expanded === row.instrumentId}
                      onToggle={() =>
                        setExpanded(expanded === row.instrumentId ? null : row.instrumentId)
                      }
                    />
                  ))}
                </tbody>
              ))}
            </table>
          </div>

          {all.length > priced.length && (
            <p className="chart-note warn">
              {all.length - priced.length} position{all.length - priced.length === 1 ? '' : 's'}{' '}
              could not be valued and are not in any total on this page — no price source quotes
              options yet.
            </p>
          )}
        </div>
      )}
    </>
  )
}

function Row({
  row,
  quote,
  accountId,
  expanded,
  onToggle,
}: {
  row: Holding
  quote: Quote | undefined
  accountId: string
  expanded: boolean
  onToggle: () => void
}) {
  const lots = useQuery({
    queryKey: ['lots', accountId, row.instrumentId],
    queryFn: () => api.lots(accountId, row.instrumentId),
    enabled: expanded,
  })

  return (
    <>
      <tr>
        <td>
          <button type="button" className="linklike" onClick={onToggle}
                  aria-expanded={expanded}>
            {expanded ? '▾' : '▸'} {row.symbol}
          </button>
          <div className="faint" style={{ fontSize: 12 }}>
            {row.assetClass} · {row.openLotCount} lot{row.openLotCount === 1 ? '' : 's'}
          </div>
        </td>
        <td className="right"><Quantity value={row.quantity} /></td>
        <td className="right num">{formatMoney(row.averageCost, row.currency)}</td>
        <td className="right"><Money value={row.costBasis} currency={row.currency} /></td>
        <td className="right">
          <QuotedPrice
            close={row.lastPrice}
            currency={row.currency}
            quote={quote}
            reason={row.priceNote}
          />
        </td>
        <td className="right">
          <Money value={row.marketValue} currency={row.currency} reason={row.priceNote} />
        </td>
        <td className="right">
          <Money value={row.unrealisedPnl} currency={row.currency} reason={row.priceNote} signed />
          {row.unrealisedPnlPercent && (
            <div style={{ fontSize: 12 }}><Percent value={row.unrealisedPnlPercent} /></div>
          )}
        </td>
        <td className="right">
          <Percent value={row.weightPercent} signed={false} reason={row.priceNote} />
        </td>
      </tr>

      {expanded && (
        <tr>
          <td colSpan={8} style={{ background: 'var(--bg-inset)' }}>
            {lots.isPending ? (
              <span className="muted">Loading lots…</span>
            ) : lots.isError ? (
              <span className="down">Could not load lots.</span>
            ) : (
              <table style={{ margin: 0 }}>
                <thead>
                  <tr>
                    <th>Opened</th>
                    <th className="right">Original</th>
                    <th className="right">Open</th>
                    <th className="right">Cost per unit</th>
                    <th>Side</th>
                  </tr>
                </thead>
                <tbody>
                  {(lots.data ?? []).map((lot) => (
                    <tr key={lot.id}>
                      <td className="num">{lot.openedOn}</td>
                      <td className="right num">{formatQuantity(lot.originalQuantity)}</td>
                      <td className="right num">{formatQuantity(lot.openQuantity)}</td>
                      <td className="right num">
                        {formatMoney(lot.costPerUnit, lot.currency)}
                      </td>
                      <td className={lot.isLong ? 'up' : 'down'}>
                        {lot.isLong ? 'LONG' : 'SHORT'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </td>
        </tr>
      )}
    </>
  )
}

function Th({
  label,
  right,
  active,
  asc,
  onClick,
}: {
  label: string
  right?: boolean
  active: boolean
  asc: boolean
  onClick: () => void
}) {
  return (
    <th className={right ? 'right' : undefined} aria-sort={active ? (asc ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className="sortable" onClick={onClick}>
        {label}
        <span className="faint">{active ? (asc ? ' ▲' : ' ▼') : ''}</span>
      </button>
    </th>
  )
}

/**
 * Sorting with unknowns.
 *
 * <p>A missing market value is not a small one. Treating null as zero would file every unpriced
 * option below the cheapest real position and imply a value it does not have, so unknowns sort
 * to the end in both directions - they are last whether the reader asked for largest or
 * smallest first, because neither question has an answer for them.
 */
function compare(key: SortKey, ascending: boolean) {
  const direction = ascending ? 1 : -1

  return (a: Holding, b: Holding): number => {
    if (key === 'symbol') {
      return a.symbol.localeCompare(b.symbol) * direction
    }
    const field = {
      quantity: (h: Holding) => h.quantity,
      costBasis: (h: Holding) => h.costBasis,
      marketValue: (h: Holding) => h.marketValue,
      unrealisedPnl: (h: Holding) => h.unrealisedPnl,
      weight: (h: Holding) => h.weightPercent,
    }[key]

    const x = field(a)
    const y = field(b)
    if (x == null && y == null) return a.symbol.localeCompare(b.symbol)
    if (x == null) return 1
    if (y == null) return -1
    return (Number(x) - Number(y)) * direction
  }
}

function Failed({ error }: { error: unknown }) {
  return (
    <div className="notice error" role="alert">
      {error instanceof ApiError ? (error.problem.detail ?? error.message) : 'Could not reach the API.'}
    </div>
  )
}
